import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { SuiteApiError } from "@weco/suite-client";
import { twinService, type TwinDispatchRequest } from "../api/twin.api";

/** Dispatches Mochi (the assistant's meeting twin) into the current call.
 *  `sendMochi` is fire-and-forget from the caller's point of view — success
 *  and failure both surface as a toast, matching the rest of the in-call
 *  control-bar actions (e.g. invite-by-email). */
export function useSendMochi() {
  const { t } = useTranslation();

  const mutation = useMutation({
    mutationFn: (body: TwinDispatchRequest) => twinService.dispatch(body),
    onSuccess: () => {
      toast.success(t("call.controls.sendMochiSuccess"));
    },
    onError: (error: unknown) => {
      // Surface the backend's own reason (e.g. "Meeting twin is disabled",
      // "Too many active twins", or the upstream video/LiveKit failure) so the
      // toast is actionable instead of a blanket "couldn't send".
      const detail =
        error instanceof SuiteApiError && error.message ? error.message : "";
      toast.error(
        detail
          ? `${t("call.controls.sendMochiError")} ${detail}`
          : t("call.controls.sendMochiError"),
      );
    },
  });

  return {
    sendMochi: mutation.mutate,
    isPending: mutation.isPending,
  };
}
